import { useCallback, useRef, useState } from 'react';
import ToastService from '../../../services/ToastService';
import GameAPI from '../GameAPI';
import {
  baseCampErrorMessage,
  baseCampFromMutation,
  createBaseCampOperationId,
} from './baseCamp';

function normalizeDepositMaterials(materials) {
  return materials
    .map(material => ({
      itemId: String(material.itemId || ''),
      quantity: Number(material.quantity) || 0,
      source: String(material.source || ''),
    }))
    .sort((left, right) => (
      left.itemId.localeCompare(right.itemId)
      || left.source.localeCompare(right.source)
      || left.quantity - right.quantity
    ));
}

function normalizeStorageTransfer(transfer) {
  const normalized = {
    direction: String(transfer?.direction || ''),
    itemId: String(transfer?.itemId || ''),
    itemInstanceId: String(transfer?.itemInstanceId || ''),
    quantity: Number(transfer?.quantity) || 0,
  };
  [
    'sourceX',
    'sourceY',
    'sourceSlotIndex',
    'destinationX',
    'destinationY',
    'destinationSlotIndex',
  ].forEach(field => {
    if (transfer?.[field] !== undefined && transfer?.[field] !== null) {
      const coordinate = Number(transfer[field]);
      if (Number.isFinite(coordinate)) normalized[field] = coordinate;
    }
  });
  return normalized;
}

export default function useBaseCampActions({
  reconcileBaseCampSnapshot,
  reconcileInventory,
  reconcileRegionTransition,
  refreshBaseCamp,
  refreshUser,
}) {
  const [campStorageOpen, setCampStorageOpen] = useState(false);
  const [campBusyBuildingKey, setCampBusyBuildingKey] = useState('');
  const [campStorageBusy, setCampStorageBusy] = useState(false);
  const [campTravelBusy, setCampTravelBusy] = useState(false);
  const [campRespawnBusy, setCampRespawnBusy] = useState(false);
  const [campActionError, setCampActionError] = useState('');
  const operationIdsRef = useRef(new Map());

  const reconcileMutation = useCallback(async (result, operationId) => {
    const snapshot = baseCampFromMutation(result);
    if (snapshot) reconcileBaseCampSnapshot(snapshot);
    else await refreshBaseCamp();

    const inventory = result?.inventory ?? result?.Inventory;
    if (Array.isArray(inventory)) {
      reconcileInventory(
        inventory,
        result?.operationId ?? result?.OperationId ?? operationId,
        result,
      );
    } else {
      await refreshUser();
    }
  }, [
    reconcileBaseCampSnapshot,
    reconcileInventory,
    refreshBaseCamp,
    refreshUser,
  ]);

  const refreshCampSafely = useCallback(async () => {
    setCampActionError('');
    try {
      return await refreshBaseCamp();
    } catch (error) {
      setCampActionError(baseCampErrorMessage(
        error,
        'Camp projects could not be refreshed.',
      ));
      return null;
    }
  }, [refreshBaseCamp]);

  const depositCampMaterials = useCallback(async building => {
    if (!building?.buildingKey || campBusyBuildingKey) return;
    const materials = [];
    (building.costs || []).forEach(cost => {
      let remaining = Math.max(
        0,
        Number(cost.required || 0) - Number(cost.contributed || 0),
      );
      const fromPack = Math.min(
        remaining,
        Math.max(0, Number(cost.availableInPack || 0)),
      );
      if (fromPack > 0) {
        materials.push({
          itemId: cost.itemId,
          quantity: fromPack,
          source: 'pack',
        });
        remaining -= fromPack;
      }
      const fromStorage = Math.min(
        remaining,
        Math.max(0, Number(cost.availableInStorage || 0)),
      );
      if (fromStorage > 0) {
        materials.push({
          itemId: cost.itemId,
          quantity: fromStorage,
          source: 'storage',
        });
      }
    });
    if (materials.length === 0) return;
    const normalizedMaterials = normalizeDepositMaterials(materials);

    const operationKey = [
      'deposit',
      building.buildingKey,
      JSON.stringify(normalizedMaterials),
    ].join(':');
    let operationId = operationIdsRef.current.get(operationKey);
    if (!operationId) {
      operationId = createBaseCampOperationId();
      operationIdsRef.current.set(operationKey, operationId);
    }

    setCampBusyBuildingKey(building.buildingKey);
    setCampActionError('');
    try {
      const result = await GameAPI.depositBaseCampBuilding(
        building.buildingKey,
        { operationId, materials: normalizedMaterials },
      );
      await reconcileMutation(result, operationId);
      operationIdsRef.current.delete(operationKey);
      ToastService.push('Available materials deposited.', 'success');
    } catch (error) {
      setCampActionError(baseCampErrorMessage(
        error,
        'The materials could not be deposited. Camp and Pack were refreshed.',
      ));
      await Promise.allSettled([refreshBaseCamp(), refreshUser()]);
    } finally {
      setCampBusyBuildingKey('');
    }
  }, [
    campBusyBuildingKey,
    reconcileMutation,
    refreshBaseCamp,
    refreshUser,
  ]);

  const startCampBuilding = useCallback(async building => {
    if (!building?.buildingKey || campBusyBuildingKey) return;
    const operationKey = `start:${building.buildingKey}`;
    let operationId = operationIdsRef.current.get(operationKey);
    if (!operationId) {
      operationId = createBaseCampOperationId();
      operationIdsRef.current.set(operationKey, operationId);
    }

    setCampBusyBuildingKey(building.buildingKey);
    setCampActionError('');
    try {
      const result = await GameAPI.startBaseCampBuilding(
        building.buildingKey,
        { operationId },
      );
      await reconcileMutation(result, operationId);
      operationIdsRef.current.delete(operationKey);
      ToastService.push(`${building.name} construction started.`, 'success');
    } catch (error) {
      setCampActionError(baseCampErrorMessage(
        error,
        'Construction could not be started. The Camp was refreshed.',
      ));
      await Promise.allSettled([refreshBaseCamp(), refreshUser()]);
    } finally {
      setCampBusyBuildingKey('');
    }
  }, [
    campBusyBuildingKey,
    reconcileMutation,
    refreshBaseCamp,
    refreshUser,
  ]);

  const cancelCampBuilding = useCallback(async building => {
    if (!building?.buildingKey || campBusyBuildingKey) return;
    const operationKey = `cancel:${building.buildingKey}`;
    let operationId = operationIdsRef.current.get(operationKey);
    if (!operationId) {
      operationId = createBaseCampOperationId();
      operationIdsRef.current.set(operationKey, operationId);
    }

    setCampBusyBuildingKey(building.buildingKey);
    setCampActionError('');
    try {
      const result = await GameAPI.cancelBaseCampBuilding(
        building.buildingKey,
        { operationId },
      );
      await reconcileMutation(result, operationId);
      operationIdsRef.current.delete(operationKey);
      ToastService.push(`${building.name} construction cancelled.`, 'info');
    } catch (error) {
      setCampActionError(baseCampErrorMessage(
        error,
        'Construction could not be cancelled. The Camp was refreshed.',
      ));
      await Promise.allSettled([refreshBaseCamp(), refreshUser()]);
    } finally {
      setCampBusyBuildingKey('');
    }
  }, [
    campBusyBuildingKey,
    reconcileMutation,
    refreshBaseCamp,
    refreshUser,
  ]);

  const transferCampStorage = useCallback(async transfer => {
    if (campStorageBusy) return;
    const normalizedTransfer = normalizeStorageTransfer(transfer);
    const operationKey = [
      'storage',
      JSON.stringify(normalizedTransfer),
    ].join(':');
    let operationId = operationIdsRef.current.get(operationKey);
    if (!operationId) {
      operationId = createBaseCampOperationId();
      operationIdsRef.current.set(operationKey, operationId);
    }

    setCampStorageBusy(true);
    setCampActionError('');
    try {
      const result = await GameAPI.transferBaseCampStorage({
        ...normalizedTransfer,
        operationId,
      });
      await reconcileMutation(result, operationId);
      operationIdsRef.current.delete(operationKey);
      ToastService.push(
        normalizedTransfer.direction === 'packToStorage'
          ? 'Item moved to your Vault.'
          : normalizedTransfer.direction === 'storageToStorage'
          ? 'Vault reordered.'
          : 'Item moved to your Pack.',
        'success',
      );
    } catch (error) {
      setCampActionError(baseCampErrorMessage(
        error,
        'The item could not be transferred. Your Vault and Pack were refreshed.',
      ));
      await Promise.allSettled([refreshBaseCamp(), refreshUser()]);
    } finally {
      setCampStorageBusy(false);
    }
  }, [
    campStorageBusy,
    reconcileMutation,
    refreshBaseCamp,
    refreshUser,
  ]);

  const travelCamp = useCallback(async (direction, requestedOperationId) => {
    if (campTravelBusy) return;
    const operationKey = `travel:${direction}`;
    let operationId = requestedOperationId
      || operationIdsRef.current.get(operationKey);
    if (!operationId) {
      operationId = createBaseCampOperationId();
      operationIdsRef.current.set(operationKey, operationId);
    }

    setCampTravelBusy(true);
    setCampActionError('');
    try {
      const result = direction === 'enter'
        ? await GameAPI.enterBaseCamp({ operationId })
        : await GameAPI.exitBaseCamp({ operationId });
      await reconcileMutation(result, operationId);
      await refreshUser();
      operationIdsRef.current.delete(operationKey);
      ToastService.push(
        direction === 'enter'
          ? 'Entered your private Camp.'
          : 'Returned to the shared region.',
        'success',
      );
      return result;
    } catch (error) {
      const message = baseCampErrorMessage(
        error,
        direction === 'enter'
          ? 'The Camp can only be entered from the settlement entrance.'
          : 'The Camp could not be left right now.',
      );
      setCampActionError(message);
      await Promise.allSettled([refreshBaseCamp(), refreshUser()]);
      const travelError = new Error(message);
      travelError.cause = error;
      throw travelError;
    } finally {
      setCampTravelBusy(false);
    }
  }, [
    campTravelBusy,
    reconcileMutation,
    refreshBaseCamp,
    refreshUser,
  ]);

  const installCampRoute = useCallback(async (
    route,
    requestedOperationId,
  ) => {
    if (!route?.regionId || campTravelBusy) return null;
    const operationKey = `install-route:${route.regionId}`;
    let operationId = requestedOperationId
      || operationIdsRef.current.get(operationKey);
    if (!operationId) {
      operationId = createBaseCampOperationId();
      operationIdsRef.current.set(operationKey, operationId);
    }

    setCampTravelBusy(true);
    setCampActionError('');
    try {
      const result = await GameAPI.installBaseCampRoute(
        route.regionId,
        { operationId },
      );
      await reconcileMutation(result, operationId);
      operationIdsRef.current.delete(operationKey);
      ToastService.push('Cableway installed.', 'success');
      return result;
    } catch (error) {
      const message = baseCampErrorMessage(
        error,
        'The cableway could not be installed. Camp and Pack were refreshed.',
      );
      setCampActionError(message);
      await Promise.allSettled([refreshBaseCamp(), refreshUser()]);
      const installError = new Error(message);
      installError.cause = error;
      throw installError;
    } finally {
      setCampTravelBusy(false);
    }
  }, [
    campTravelBusy,
    reconcileMutation,
    refreshBaseCamp,
    refreshUser,
  ]);

  const travelCampRoute = useCallback(async (
    route,
    requestedOperationId,
  ) => {
    if (!route?.regionId || campTravelBusy) return null;
    const operationKey = `travel-route:${route.regionId}`;
    let operationId = requestedOperationId
      || operationIdsRef.current.get(operationKey);
    if (!operationId) {
      operationId = createBaseCampOperationId();
      operationIdsRef.current.set(operationKey, operationId);
    }

    setCampTravelBusy(true);
    setCampActionError('');
    try {
      const result = await GameAPI.travelBaseCampRoute(
        route.regionId,
        { operationId },
      );
      await reconcileRegionTransition?.(result);
      operationIdsRef.current.delete(operationKey);
      ToastService.push(`Travelled to ${route.regionName}.`, 'success');
      return result;
    } catch (error) {
      const message = baseCampErrorMessage(
        error,
        'The Camp cableway could not be used right now.',
      );
      setCampActionError(message);
      await Promise.allSettled([refreshBaseCamp(), refreshUser()]);
      const travelError = new Error(message);
      travelError.cause = error;
      throw travelError;
    } finally {
      setCampTravelBusy(false);
    }
  }, [
    campTravelBusy,
    reconcileRegionTransition,
    refreshBaseCamp,
    refreshUser,
  ]);

  const enterCamp = useCallback(
    options => travelCamp('enter', options?.operationId),
    [travelCamp],
  );
  const exitCamp = useCallback(
    options => travelCamp('exit', options?.operationId),
    [travelCamp],
  );

  const setCampRespawnPoint = useCallback(async () => {
    if (campRespawnBusy) return null;
    const operationKey = 'set-respawn-point';
    let operationId = operationIdsRef.current.get(operationKey);
    if (!operationId) {
      operationId = createBaseCampOperationId();
      operationIdsRef.current.set(operationKey, operationId);
    }

    setCampRespawnBusy(true);
    setCampActionError('');
    try {
      const result = await GameAPI.setBaseCampRespawnPoint({ operationId });
      await reconcileMutation(result, operationId);
      operationIdsRef.current.delete(operationKey);
      ToastService.push('This Camp is now your respawn point.', 'success');
      return result;
    } catch (error) {
      const message = baseCampErrorMessage(
        error,
        'The respawn point could not be changed. The Camp was refreshed.',
      );
      setCampActionError(message);
      await Promise.allSettled([refreshBaseCamp(), refreshUser()]);
      const respawnError = new Error(message);
      respawnError.cause = error;
      throw respawnError;
    } finally {
      setCampRespawnBusy(false);
    }
  }, [
    campRespawnBusy,
    reconcileMutation,
    refreshBaseCamp,
    refreshUser,
  ]);

  const openCampStorage = useCallback(() => {
    setCampActionError('');
    setCampStorageOpen(true);
  }, []);

  const closeCampStorage = useCallback(() => {
    if (!campStorageBusy) setCampStorageOpen(false);
  }, [campStorageBusy]);

  return {
    campActionError,
    campBusyBuildingKey,
    campStorageBusy,
    campStorageOpen,
    campTravelBusy,
    campRespawnBusy,
    cancelCampBuilding,
    closeCampStorage,
    depositCampMaterials,
    enterCamp,
    exitCamp,
    installCampRoute,
    openCampStorage,
    refreshCampSafely,
    setCampRespawnPoint,
    startCampBuilding,
    transferCampStorage,
    travelCampRoute,
  };
}
